/**
 * Data access for the performance dashboard.
 *
 * Everything here is polled rather than pushed. The underlying data only changes once a minute —
 * that is when each API flushes its accumulated totals — so a live feed would carry the same
 * numbers over and over. The refresh intervals below are matched to that: no faster than the data
 * can change, which is also why the page does not become a load source of its own.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { performanceApi } from '@api/endpoints/performance'
import type {
  EndpointSort,
  PerfProbeTargetRequest,
  QuerySort,
} from '@api/types/performance'

/** Query key root, so one invalidation can refresh every panel after an operator action. */
const ROOT = 'performance'

/**
 * The roll-up tables gain a row a minute, so anything reading them is stale for at most that long.
 * Refetching faster only costs queries against the database the page exists to protect.
 */
const ROLLUP_REFRESH_MS = 60_000

/** Live server state, which does change second to second. */
const LIVE_REFRESH_MS = 15_000

export function usePerfOverview(hours: number, service?: string) {
  return useQuery({
    queryKey: [ROOT, 'overview', hours, service],
    queryFn: () => performanceApi.getOverview(hours, service),
    refetchInterval: ROLLUP_REFRESH_MS,
  })
}

export function usePerfEndpoints(hours: number, sort: EndpointSort, service?: string) {
  return useQuery({
    queryKey: [ROOT, 'endpoints', hours, sort, service],
    queryFn: () => performanceApi.getEndpoints({ hours, sort, service, limit: 500 }),
    refetchInterval: ROLLUP_REFRESH_MS,
  })
}

/**
 * Latency over time. `route` narrows it to one endpoint; leaving it undefined charts everything,
 * which is the line that shows a system-wide change.
 */
export function usePerfSeries(hours: number, route?: string, method?: string, service?: string) {
  return useQuery({
    queryKey: [ROOT, 'series', hours, route, method, service],
    queryFn: () => performanceApi.getSeries({ hours, route, method, service }),
    refetchInterval: ROLLUP_REFRESH_MS,
  })
}

export function usePerfQueries(hours: number, sort: QuerySort, service?: string) {
  return useQuery({
    queryKey: [ROOT, 'queries', hours, sort, service],
    queryFn: () => performanceApi.getQueries({ hours, sort, service, limit: 200 }),
    refetchInterval: ROLLUP_REFRESH_MS,
  })
}

export function usePerfSlowRequests(hours: number, service?: string, route?: string) {
  return useQuery({
    queryKey: [ROOT, 'slow', hours, service, route],
    queryFn: () => performanceApi.getSlowRequests({ hours, service, route, limit: 300 }),
    refetchInterval: ROLLUP_REFRESH_MS,
  })
}

/** One slow request with its query breakdown. Only fetched when a row is opened. */
export function usePerfSlowRequest(id: number | null) {
  return useQuery({
    queryKey: [ROOT, 'slow', 'detail', id],
    queryFn: () => performanceApi.getSlowRequest(id as number),
    enabled: id != null,
  })
}

/**
 * The database snapshot.
 *
 * Polled faster than the roll-ups because most of it — running queries, connection counts — is a
 * live reading rather than an aggregate, and a query that has been running for ninety seconds is
 * only useful to see while it is still running.
 */
export function usePerfDatabase(enabled: boolean, top = 25) {
  return useQuery({
    queryKey: [ROOT, 'database', top],
    queryFn: () => performanceApi.getDatabase(top),
    refetchInterval: LIVE_REFRESH_MS,
    enabled,
  })
}

export function usePerfProbes(hours: number) {
  return useQuery({
    queryKey: [ROOT, 'probes', hours],
    queryFn: () => performanceApi.getProbes(hours),
    refetchInterval: 30_000,
  })
}

export function useCreateProbe() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: PerfProbeTargetRequest) => performanceApi.createProbe(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [ROOT, 'probes'] }),
  })
}

export function useUpdateProbe() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PerfProbeTargetRequest }) =>
      performanceApi.updateProbe(id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [ROOT, 'probes'] }),
  })
}

export function useDeleteProbe() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => performanceApi.deleteProbe(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [ROOT, 'probes'] }),
  })
}

export function useRunProbe() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => performanceApi.runProbe(id),
    // The manual run is recorded like a scheduled one, so the list has to be re-read to show it.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [ROOT, 'probes'] }),
  })
}

export function useResetStatementStats() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => performanceApi.resetStatementStats(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [ROOT, 'database'] }),
  })
}

/**
 * Flush the admin API's current partial minute.
 *
 * Only this host. The User API flushes on its own timer and there is no channel from the portal to
 * tell it otherwise, so its newest minute can still be up to a minute behind after this returns.
 */
export function useFlushMetrics() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => performanceApi.flush(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [ROOT] }),
  })
}
